/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */
<template>
  <form
    class="form-horizontal"
    aria-labelledby="customer-admin-usersheader"
    @submit.prevent="save"
  >
    <h2
      id="customer-admin-invitesheader"
      class="sr-only"
    >
      Add {{ isEdu ? 'Instructor' : 'Coach' }}
    </h2>
    <div
      class="form-group"
      style="padding: 20px 0 0 20px;"
    >
      Use the form below to generate an email allowing the recipient
      to create a new {{ isEdu ? 'Instructor' : 'Coach' }} account.
    </div>
    <div
      class="form-group"
      style="padding: 0 20px;"
    >
      <label
        for="inviteEmail"
        class="control-label"
      >
        Email
      </label>
      <input
        id="inviteEmail"
        v-model.trim="email"
        type="email"
        class="form-control"
        placeholder="username@domain.com"
        name="email"
        style="width: 300px;"
        required
      >
    </div>
    <div
      class="form-group"
      style="padding: 0 20px;"
    >
      <label class="control-label">
        {{ 'Can add new ' + (isEdu ? 'classes' : 'groups') }}
      </label><br>
      <label
        for="orgLevelManagerRights1"
        class="radio-inline lower"
      >
        <input
          id="orgLevelManagerRights1"
          v-model="roles[0].roles[0]"
          type="radio"
          value=""
        >No
      </label>
      <label
        for="orgLevelManagerRights2"
        class="radio-inline lower"
      >
        <input
          id="orgLevelManagerRights2"
          v-model="roles[0].roles[0]"
          type="radio"
          value="manager"
        >Yes
      </label>
    </div>
    <div
      class="form-group"
      style="padding: 0 20px;"
    >
      <label class="control-label">
        Make user {{ isEdu ? 'an instructor' : 'a coach' }} in the following {{ isEdu ? 'classes' : 'groups' }}:
      </label><br>

      <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
      <div
        v-for="role in roles"
        v-if="role.groupId"
        :key="role.groupId"
        class="form-group"
        style="padding: 0 20px; margin: 0;"
      >
        <input
          :id="'addInstructorGroup' + role.groupId"
          v-model="role.roles"
          type="checkbox"
          value="manager"
        >
        <label
          :for="'addInstructorGroup' + role.groupId"
          class="lower"
        >
          {{ groupIdsToNames[role.groupId] }}
        </label>
      </div>
    </div>

    <loading-button :disabled="warnings.length > 0 || isSending">
      Send Invite
    </loading-button>

    <div
      class="help-block"
      style="padding-top: 10px;"
    >
      <p
        v-for="warning in warnings"
        :key="warning"
        class="text-warning"
        role="alert"
      >
        <svg
          class="icon-plus"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-plus" />
        </svg>
        {{ warning }}
      </p>
      <p
        v-if="successMsg"
        class="text-success"
        role="alert"
      >
        <svg
          class="icon-check"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-check" />
        </svg>
        {{ successMsg }}
      </p>
    </div>
  </form>
</template>

<style lang="less" scoped>
.icon-check {
  fill: #04D97E !important;
  position: relative;
  top: 2px;
  height: 16px; width: 16px;
}
.icon-plus {
  transform: rotate(45deg);
  fill: #D3382F !important;
  position: relative;
  top: 2px;
  height: 16px; width: 16px;
}
</style>

<script>
export default {
  props: {
    org: {
      type: Object,
      required: true
    },
    isEdu: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      required: true
    },
    groupIdsToNames: Object,
  },
  data() {
    return {
      isSending: false,
      successMsg: null,
      email: '',
      // an invite has 'roles' which is a list of objects,
      // each representing a single group ('groupId' prop) with a list of 'roles' (such as 'manager' or 'speaker') for that group.
      // put org-level pseudo group at front. The rest will populate after a call to list groups
      roles: [{
        groupId: null,
        roles: ['manager']
      }]
    };
  },
  computed: {
    warnings() {
      const warnings = [];
      const allRoles = this._.flatMap(this.roles, role => role.roles);
      const atLeastOneGroupSelected = this._.includes(allRoles, 'manager');
      if (!atLeastOneGroupSelected) {
        warnings.push('Grant ' + (this.isEdu ? 'Instructor' : 'Coach') + ' access to one or more ' + (this.isEdu ? 'classes' : 'groups') + ' or select the option to allow the user to create new ' +
            (this.isEdu ? 'classes' : 'groups') + '.');
      }
      if (warnings.length > 0) {
        /* eslint-disable-next-line no-side-effects-in-computed-properties */
        this.successMsg = null;
      }
      return warnings;
    }
  },
  created() {
    // map the groups that were provided as a prop into a "roles" array for the invite object bound to the form.
    this._.forEach(this.groups, g => {
      this.roles.push({
        groupId: g.groupId,
        roles: []
      });
    });
  },
  methods: {
    save() {
      this.isSending = true;

      const invite = {
        email: this.email,
        // filter out groups (this.roles) that are not selected
        roles: this._.filter(this.roles, r => {
          return r.roles.length > 0 && r.roles[0] !== '';
        })
      };
      if (invite.roles.length > 0) {
        this.$store.dispatch('createInvite', invite).then(() => {
          this.isSending = false;

          // tell user "invite has been sent to this.email"
          this.successMsg = `Invite has been sent to ${this.email}.`;
          // reset the form
          this.email = null;
          this._.forEach(this.roles, r => {
            // the org-level group will have "yes" radio preselected
            r.roles = (r.groupId === null)? ['manager'] : [];
          });
          // Only show the success message for 8 seconds
          window.setTimeout(() => {this.successMsg = null;}, 8000);
        });
      } else {
        this.isSending = false;
      }
    }
  }
};
</script>
